import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import moveArrayItem from 'array-move';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { useBoardService } from 'src/features/boards';
import { useKanbanStore } from 'src/features/kanban';
import { KanbanState } from 'src/features/kanban/store/types';
import { findColumnIndexById } from 'src/features/kanban/utils/findColumnIndexById';
import {
  addCustomField,
  createSubTask,
  deleteCustomField,
  updateCustomFieldData,
  updateCustomFieldName,
  updateTaskProperties,
} from 'src/features/tasks/api/useTasksService';
import { useTasksStore } from 'src/features/tasks/store/useTasksStore';
import { Task, TypeEnum } from 'src/features/tasks/types/tasks';
import { v4 as uuid } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const {
  addColumn,
  addTask,
  deleteColumn,
  deleteTask,
  duplicateTask,
  fetchBoardById,
  markTaskAsComplete,
  updateColumn,
  updateColumnOrder,
} = useBoardService();

const initialState = {
  tableColumns: [
    {
      Header: '',
      accessor: 'initialState',
    },
  ],
  columns: [],
  fields: [],
  labels: [],
  boardTableId: '',
  isReadOnly: false,
};

export const useTableStore = create<
  TableStore,
  [['zustand/devtools', TableStore], ['zustand/immer', TableStore]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      ...initialState,
      initialize: (tableColumns) =>
        set(() => ({
          tableColumns,
        })),
      setLabels: (labels) => {
        set(() => [labels]);
      },
      setReadOnly: (value) => {
        set(() => ({
          isReadOnly: value,
        }));
      },
      setColumns: (columns, newFields) =>
        set((state) => {
          const { fields } = state;
          // const newFieldIds = newFields.map((newField) => newField.fieldId);
          // const isEqual = fields.some((field) => newFieldIds.includes(field.fieldId));
          // if (newFields[0]?.fieldId) {
          const newFieldsIds = newFields.map((i) => i.fieldId + i.label);
          const fieldsIds = fields.map((i) => i.fieldId + i.label);
          if (!isEqual(newFieldsIds.sort(), fieldsIds.sort())) {
            return {
              columns,
              fields: newFields,
            };
          }
          return {
            columns,
          };
        }),
      setFields: (newFields) =>
        set((state) => {
          const { fields } = state;
          const newFieldsIds = newFields.map((i) => i.fieldId + i.label);
          const fieldsIds = fields.map((i) => i.fieldId + i.label);
          if (!isEqual(newFieldsIds.sort(), fieldsIds.sort())) {
            return {
              fields: newFields,
            };
          }
          return {
            fields,
          };
        }),
      onAddTableColumn: (column) => {
        set((state) => {
          const { tableColumns } = state;
          tableColumns.push(column);
        });
      },
      onChangeTableColumnFields: (fields) => {
        set((state) => {
          state.tableColumns.splice(state.tableColumns.length - 1, 0, ...fields);
        });
      },
      onAddCustomField: async (type, label, boardId) => {
        const { columns, boardTableId } = get();
        const fieldId = uuid();
        const taskId: any = columns[0]?.items[0]?.id || '';
        const task = columns[0]?.items[0];
        set((state) => {
          state.fields = [
            ...state.fields,
            {
              fieldId,
              label,
              type,
            },
          ];
        });
        await addCustomField(
          taskId,
          fieldId,
          type,
          label,
          '',
          (task?.fields || []).length,
          boardTableId
        );
        get().fetchBoard(boardId);
      },
      fetchBoard: async (boardId) => {
        const board = await fetchBoardById({
          id: get().boardTableId,
        });
        set(() => ({
          columns: board?.columns,
        }));
      },
      setItem: async ({ columnId, itemId, item }) => {
        set((s) => {
          const colIndex = s.columns.findIndex((obj) => obj.id === columnId);
          const itemIndex = s.columns[colIndex]?.items?.findIndex((obj) => obj.id === itemId);
          if (colIndex > -1 && itemIndex > -1) {
            s.columns[colIndex].items[itemIndex] = {
              ...s.columns[colIndex].items[itemIndex],
              ...item,
            };
          } else if (colIndex > -1 && itemIndex < 0) {
            const itemWithSubItemIndex = s.columns[colIndex]?.items?.findIndex((i) =>
              (i.subtasks || []).find((st: any) => st._id === itemId)
            );
            if (itemWithSubItemIndex > -1) {
              const subItemIndex = (
                s.columns[colIndex]?.items[itemWithSubItemIndex].subtasks || []
              ).findIndex((st: any) => st._id === itemId);
              (s.columns[colIndex].items[itemWithSubItemIndex] as any).subtasks[subItemIndex] = {
                ...(s.columns[colIndex].items[itemWithSubItemIndex] as any).subtasks[subItemIndex],
                ...item,
              };
            }
          }
        });
      },
      onUpdateItemField: async ({ item, keys }) => {
        const payload: any = {};
        keys.map((field) => {
          if (item[field] === undefined) {
            payload[field] = null;
          } else if (field === 'assignee') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'duplicate') {
            payload[field] = item[field]?.map((obj) => obj.memberId);
          } else if (field === 'assigner') {
            payload[field] = item.assigner;
          } else if (field === 'assignDate') {
            if (item?.assignDate?.length && item?.assignDate?.length > 1) {
              payload.start_date = item?.assignDate?.[0];
              payload.end_date = item?.assignDate?.[1];
            } else {
              payload.end_date = item?.assignDate?.[0] || null;
              payload.start_date = null;
            }
          } else if (field === 'estimates') {
            payload[field] = item[field]?.map((i) => {
              if (i.type === 'member') {
                return Object.assign({}, i, { member: i?.member });
              } else return i;
            });
          } else {
            payload[field] = item[field];
          }
        });
        payload.id = item.id;
        if (item.id) {
          await updateTaskProperties({ properties: payload, taskId: item.id });
        }
      },
      onMarkAsComplete: async ({ columnId, completed = true, itemId }) => {
        set((state) => {
          const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
              (obj) => obj.id === itemId
            );
            state.columns[foundColumnIndex].items[foundTaskIndex].completed = completed;
          }
        });
        const task = await markTaskAsComplete(itemId, completed);
        task && get().setItem({ columnId, itemId, item: task });
      },
      onMarkAsCompleteSubTask: async ({ columnId, parentId, completed, itemId }) => {
        set((state) => {
          const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
              (obj) => obj.id === parentId
            );
            const foundSubTaskIndex = state.columns[foundColumnIndex].items[
              foundTaskIndex
            ]?.subtasks?.findIndex((obj) => obj.id === itemId);
            if (foundSubTaskIndex !== undefined && foundSubTaskIndex > -1) {
              (state.columns[foundColumnIndex].items[foundTaskIndex].subtasks as any)[
                foundSubTaskIndex
              ].completed = completed;
            }
          }
        });
        const task = await markTaskAsComplete(itemId, completed);
      },
      onUpdateCustomFieldData: async (task, fieldId, data) => {
        set(async () => {
          if (task && task.id) {
            if (!task.fields) {
              task.fields = [];
            }
            const field = task.fields.find((item) => item.fieldId === fieldId);
            field && data !== undefined && (field.data = data);
            let columnId = task.colId;
            // TODO: Check if this needs to be on a cell editor level
            if (task.isSubtask && (task as any).parentColumn) {
              columnId = (task as any).parentColumn;
            }
            get().setItem({ columnId, itemId: task.id, item: task });
          }
        });
        if (task && task.id) {
          if (!task.fields) {
            task.fields = [];
          }
          await updateCustomFieldData(task.id, fieldId, data);
        }
      },
      onColumnOrder: (source, destination) => {
        set((state) => {
          const { tableColumns } = state;
          const element = tableColumns[source];
          tableColumns.splice(source, 1);
          tableColumns.splice(destination, 0, element);
        });
      },
      onHideColumn: (accessor) => {
        set((state) => {
          const { tableColumns } = state;
          const tableColumnIds = [...tableColumns].map((tableColumn) => tableColumn.accessor);
          const index = tableColumnIds.indexOf(accessor);
          tableColumns[index].isHidden = !tableColumns[index].isHidden;
        });
      },
      onColumnResize: (width, accessor, isResizing) => {
        set((state) => {
          const { tableColumns } = state;
          const newTableColumns = [...tableColumns];
          const index = tableColumns.findIndex((tableColumn) => tableColumn.accessor === accessor);
          if (index > -1 && width) {
            if (tableColumns[index].width !== width && !isResizing) {
              if (width > 500) width = 500;
              newTableColumns[index] = {
                ...tableColumns[index],
                width,
              };
              return {
                tableColumns: newTableColumns,
              };
            }
          }
        });
      },
      onAddNewItem: async ({ boardId, columnKey, ...otherProps }) => {
        let foundColumnIndex = -1;
        set((state) => {
          foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnKey);
          if (foundColumnIndex > -1) {
            state.columns[foundColumnIndex].items.push({
              id: uuid(),
              comments: [],
              ...otherProps,
            });
          }
        });

        if (foundColumnIndex > -1) {
          const payload = {
            boardId: get().boardTableId,
            colId: columnKey,
            type: otherProps.type,
            taskName: otherProps.name,
            assigner: otherProps.assigner?.memberId,
            assignee: otherProps.assignee?.map((obj) => obj.memberId),
            duplicate: otherProps.duplicate?.map((obj) => obj.memberId),
            startDate: otherProps?.assignDate?.[0],
            endDate: otherProps?.assignDate?.[1],
            description: otherProps.description,
            priority: otherProps.priority,
            label: otherProps.label,
            creation: otherProps.creation?.creationId,
            attachments: otherProps.attachments,
            estimates: otherProps.estimates,
            top: otherProps.top,
          };

          const task = await addTask(payload as any);
          if (task) get().fetchBoard(boardId);
        }
      },
      onAddNewSubItem: async ({ columnId, parentId }) => {
        let foundColumnIndex = -1,
          foundItemIndex = -1;
        set((state) => {
          foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            foundItemIndex = state.columns[foundColumnIndex].items.findIndex(
              (obj) => obj.id === parentId
            );
            if (foundItemIndex > -1) {
              state.columns[foundColumnIndex].items[foundItemIndex].subtasks?.push({
                name: [{ children: [{ text: 'Untitled' }] }],
                id: uuid(),
                type: 'task',
                isLoading: true,
                parent: parentId,
              });
            }
          }
        });

        if (foundColumnIndex > -1) {
          const task = await createSubTask(parentId, 'Untitled');
          if (task) get().fetchBoard(get().boardTableId);
        }
      },
      onChangeColumnName: async ({ id, name, boardId }) => {
        set((state) => {
          const foundIndex = findColumnIndexById(state.columns, id);
          const { columns } = state;
          if (foundIndex > -1) {
            columns[foundIndex].name = name;
          }
        });
        const response = await updateColumn({ colKey: 'name', colProperties: { name }, id });
        if (response) {
          get().fetchBoard(boardId);
        }
      },
      onChangeColumnColor: async ({ color, id, boardId }) => {
        let foundColIndex = -1;
        set((state) => {
          foundColIndex = findColumnIndexById(state.columns, id);
          if (foundColIndex > -1) {
            state.columns[foundColIndex].color = color;
          }
        });
        const board = await updateColumn({ colKey: 'color', colProperties: { color }, id });
        if (board) {
          get().fetchBoard(boardId);
        }
      },
      onUpdateCustomFieldName: async (task, fieldId, name) => {
        set((state) => {
          // const { tableColumns } = state;
          // const index = tableColumns.findIndex((tableColumn) => tableColumn.accessor === fieldId);
          // if (index > -1) {
          //   tableColumns[index].name = name;
          // }
          const { fields } = state;
          const fieldIndex = fields.findIndex((item) => item.fieldId === fieldId);
          if (fieldIndex > -1) {
            state.fields[fieldIndex].label = name;
          }
          const taskList = state.columns.map((i) => i.items).flat();
          if (taskList?.[0]?.id) {
            useTasksStore.getState().onUpdateCustomFieldName({
              taskId: taskList?.[0]?.id,
              fieldId,
              name,
            });
          }
        });
      },

      fetchLabel: async ({ id }) => {
        const response = await useBoardService().useLabelsForBoards({
          id,
          isTaskTemplateId: false,
        });
        const data = response?.data?.data;
        if (data) {
          const labelData = Object.keys(data).map((item) => ({
            id: item,
            label: data[item].label,
            value: data[item].label,
            color: data[item].color,
          }));

          set((s) => {
            s.labels = labelData;
          });
        }
      },
      onAddNewColumn: async ({ boardId, color, name }) => {
        set((state) => {
          const id = uuid();
          const { columns } = state;
          columns.push({
            name,
            items: [],
            id: id,
            color,
          });
        });
        const board = await addColumn({ boardId: get().boardTableId, name, color });
        if (board) {
          set(() => ({
            columns: board?.columns,
          }));
        }
      },
      onChangeColumnOrder: async ({ columnIds, boardId }) => {
        set((state) => {
          const sortedColumns = state.columns.sort((a, b) => {
            return columnIds.indexOf(a.id) - columnIds.indexOf(b.id);
          });
          state.columns = sortedColumns;
        });
        const board = await updateColumnOrder({ boardId: get().boardTableId, columnIds });
        if (board) {
          get().fetchBoard(boardId);
        }
      },
      onUpdateItemPlacementState: ({ sourceColId, destColId, itemId, sourceIndex, destIndex }) => {
        const columns = JSON.parse(JSON.stringify(get().columns));
        const sourceColIndex = findColumnIndexById(columns, sourceColId);
        if (sourceColIndex < 0) return;
        const destColIndex = findColumnIndexById(columns, destColId);
        if (destColIndex < 0) return;
        if (isNil(sourceIndex))
          sourceIndex = columns[sourceColIndex].items.findIndex((obj) => obj.id === itemId);
        if (isNil(sourceIndex)) return;
        if (sourceIndex < 0) {
          /** CHECK IF TASK IN SUBTASK */
          const parentColIndex = columns.findIndex((obj) =>
            (obj.items || []).find((item) => item.subtasks?.find((st) => st.id === itemId))
          );
          if (parentColIndex < 0) return;
          const parentIndex = columns[parentColIndex].items.findIndex((obj) =>
            (obj.subtasks || []).find((st) => st.id === itemId)
          );
          if (parentIndex < 0) return;
          const parentItem = columns[parentColIndex].items[parentIndex];
          columns[parentColIndex].items[parentIndex].subtasks = parentItem.subtasks?.map((t) => {
            if (t.id === itemId) {
              return {
                ...t,
                column: destColId,
              };
            }
            return t;
          });
          set(() => ({
            columns,
          }));
          return;
        }
        if (sourceColId !== destColId) {
          const item = columns[sourceColIndex].items[sourceIndex];
          item.column = destColId;
          columns[destColIndex].items.splice(destIndex, 0, item);
          columns[sourceColIndex].items = columns[sourceColIndex].items.filter(
            (obj) => obj.id !== itemId
          );
        } else if (sourceColId === destColId) {
          columns[sourceColIndex].items = moveArrayItem(
            columns[sourceColIndex].items,
            sourceIndex,
            destIndex
          );
        }
        set(() => ({
          columns,
        }));
      },
      onUpdateItemPlacement: async ({
        sourceColId,
        destColId,
        itemId,
        sourceIndex,
        destIndex,
        boardId,
      }) => {
        get().onUpdateItemPlacementState({
          sourceColId: sourceColId,
          destColId: destColId,
          itemId: itemId,
          sourceIndex: sourceIndex,
          destIndex: destIndex,
        });
        await useKanbanStore.getState().onUpdateItemPlacement({
          sourceColId,
          destColId,
          itemId,
          sourceIndex,
          destIndex,
          boardId,
        });
      },
      onDeleteColumn: async ({ columnId, boardId }) => {
        let foundColIndex = -1;
        set((state) => {
          foundColIndex = findColumnIndexById(state.columns, columnId);
          if (foundColIndex > -1) {
            state.columns = state.columns.filter((obj) => obj.id !== columnId);
          }
        });

        if (foundColIndex > -1) {
          const board = await deleteColumn({ colId: columnId });
          if (board) {
            get().fetchBoard(boardId);
          }
        }
      },
      onDeleteItem: async ({ columnId, itemId }) => {
        let foundColumnIndex = -1;
        set((state) => {
          foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            state.columns[foundColumnIndex].items = state.columns[foundColumnIndex].items.filter(
              (item) => item.id !== itemId
            );
          }
        });
        if (foundColumnIndex > -1) {
          await deleteTask({ id: itemId });
        }
      },
      onDeleteSubItem: async ({ columnId, itemId, parentId }) => {
        let foundColumnIndex = -1;
        set((state) => {
          foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
              (obj) => obj.id === parentId
            );
            const foundSubTaskIndex = state.columns[foundColumnIndex].items[
              foundTaskIndex
            ].subtasks?.findIndex((obj) => obj.id === itemId);
            if (foundSubTaskIndex !== undefined && foundSubTaskIndex > -1) {
              state.columns[foundColumnIndex].items[foundTaskIndex].subtasks = state.columns[
                foundColumnIndex
              ].items[foundTaskIndex].subtasks?.filter((item) => item.id !== itemId);
            }
          }
        });
        if (foundColumnIndex > -1) {
          await deleteTask({ id: itemId });
        }
      },
      onDuplicateItem: async ({ columnId, itemId }) => {
        let foundColumnIndex = -1;
        set(async (state) => {
          foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
          if (foundColumnIndex > -1) {
            const newTask = await duplicateTask({ id: itemId });
            newTask && state.columns[foundColumnIndex].items.push(newTask);
          }
        });
      },
      onDeleteCustomField: async ({ taskId, fieldId }) => {
        set((state) => {
          const { fields } = state;
          state.fields = fields.filter((item) => item.fieldId !== fieldId);
        });
        await deleteCustomField(taskId, fieldId);
      },
      removeItemsFromBoard: (itemIds) => {
        set((state) => {
          const columns = JSON.parse(JSON.stringify(state.columns));
          columns.forEach((column) => {
            column.items = column.items.filter((item) => !itemIds.includes(item.id));
          });
          state.columns = columns;
        });
      },
    }))
  )
);

interface TableStore {
  tableColumns: any;
  columns: KanbanState['columns'];
  fields: any[];
  labels: KanbanState['labels'];
  boardTableId: string;
  isReadOnly: boolean;
  onAddTableColumn: (column: any) => void;
  initialize: (tableColumns: any) => void;
  setColumns: (columns: any, newFields: any) => void;
  setFields: (newFields: any) => void;
  setReadOnly: (value: boolean) => void;
  onChangeTableColumnFields: (fields: any) => void;
  onAddCustomField: (type: TypeEnum, label: string, boardId: any) => Promise<void>;
  setItem: (arg: { columnId: any; itemId: any; item: Partial<Task> }) => void;
  onUpdateItemField: (arg: { item: Partial<Task>; keys: Array<keyof Task> }) => any;
  onMarkAsComplete: (arg: {
    columnId: string;
    itemIndex?: number;
    completed: boolean;
    itemId: string;
  }) => void;
  onMarkAsCompleteSubTask: (arg: {
    columnId: string;
    parentId: string;
    itemIndex?: number;
    completed: boolean;
    itemId: string;
  }) => void;
  onUpdateCustomFieldData: (
    task: Partial<Task> & { colId?: string },
    fieldId: any,
    data: any
  ) => Promise<void>;
  onColumnOrder: (source: number, destination: number) => void;
  onHideColumn: (accessor: any) => void;
  onColumnResize: (width: number, accessor: string, isResizing: any) => void;
  fetchBoard: (boardId: any) => Promise<void>;
  onAddNewItem: (arg: any) => void;
  onAddNewSubItem: (arg: any) => void;
  onChangeColumnName: (arg: { id: any; name: any; boardId: any }) => Promise<void>;
  onChangeColumnColor: (arg: { color: any; id: any; boardId: any }) => Promise<void>;
  onUpdateCustomFieldName: (task: any, fieldId: any, name: any) => Promise<void>;
  fetchLabel: (arg: { id: string }) => Promise<void>;
  setLabels: (lables: any) => void;
  onAddNewColumn: (arg: { boardId: any; color: string; name: string }) => void;
  onChangeColumnOrder: (arg: { columnIds: any; boardId: any }) => Promise<void>;
  onUpdateItemPlacement: (arg: {
    sourceColId: string;
    destColId: string;
    itemId: string;
    sourceIndex?: number;
    destIndex: number;
    boardId: any;
  }) => void;
  onUpdateItemPlacementState: (arg: {
    sourceColId: string;
    destColId: string;
    itemId: string;
    sourceIndex?: number;
    destIndex: number;
  }) => void;
  onDeleteColumn: (arg: any) => Promise<void>;
  onDeleteItem: (arg: any) => Promise<void>;
  onDeleteSubItem: (arg: any) => Promise<void>;
  onDuplicateItem: (arg: any) => Promise<void>;
  onDeleteCustomField: (arg: { taskId: string; fieldId: string }) => Promise<void>;
  removeItemsFromBoard: (itemIds: string[]) => void;
}
